// src/lib/profile.ts
import { client } from '../lib/AmplifyClient';
import type { Schema } from '../../amplify/data/resource';

type Profile = Schema['Profile']['type'];

function normEmail(v?: string | null) {
  const e = (v ?? '').trim().toLowerCase();
  return e || null;
}

/** Find by secondary index: sub (Cognito userId). */
async function findBySub(sub?: string | null): Promise<Profile | null> {
  const s = (sub ?? '').trim();
  if (!s) return null;
  try {
    const res = await (client as any).models.Profile.list({
      filter: { sub: { eq: s } },
      limit: 1,
    });
    const items = (res?.data ?? []) as Profile[];
    return items[0] ?? null;
  } catch {
    return null;
  }
}

/** Find by secondary index: email (normalized). */
async function findByEmail(email?: string | null): Promise<Profile | null> {
  const e = normEmail(email);
  if (!e) return null;
  try {
    const res = await (client as any).models.Profile.list({
      filter: { email: { eq: e } },
      limit: 1,
    });
    const items = (res?.data ?? []) as Profile[];
    return items[0] ?? null;
  } catch {
    return null;
  }
}

/** Primary identifier lookup: get by emailLower. */
async function getByEmailLower(emailLower?: string | null): Promise<Profile | null> {
  const key = (emailLower ?? '').trim().toLowerCase();
  if (!key) return null;
  try {
    const res = await (client as any).models.Profile.get({ emailLower: key } as any);
    return (res?.data as Profile) ?? null;
  } catch {
    return null;
  }
}

/** Public: best-effort fetch of my Profile using sub → emailLower → email. */
export async function getMyProfile({
  userId, // Cognito sub
  email,
}: {
  userId?: string | null;
  email?: string | null;
}): Promise<Profile | null> {
  // 1) Prefer secondary index lookup by sub
  const bySub = await findBySub(userId ?? null);
  if (bySub) return bySub;

  // 2) Try identifier get by emailLower (derived from email)
  const lower = normEmail(email);
  if (lower) {
    const byLower = await getByEmailLower(lower);
    if (byLower) return byLower;
  }

  // 3) Fallback to secondary index lookup by email (some older records)
  const byEmail = await findByEmail(email ?? null);
  if (byEmail) return byEmail;

  return null;
}

/**
 * Create or update the caller's Profile.
 * - Uses emailLower as the identifier (PK).
 * - Preserves/normalizes email; records sub.
 * - Uses userPool auth for writes.
 */
export async function upsertMyProfile({
  userId, // Cognito sub
  email,
  patch,
}: {
  userId: string;
  email: string;
  patch: Partial<Profile>;
}) {
  const e = normEmail(email) as string;
  const emailLower = e; // normalized email is our identifier
  const existing = await getMyProfile({ userId, email: e });

  if (existing) {
    // Update — identifier must be emailLower
    const idKey = (existing as any)?.emailLower || emailLower;
    return (client as any).models.Profile.update(
      {
        emailLower: idKey,
        // never silently drop existing email; but keep it normalized
        email: (existing as any)?.email || e,
        sub: userId,
        ...patch,
      } as any,
      { authMode: 'userPool' } as any
    );
  }

  // Create — identifier must be emailLower; include sub
  return (client as any).models.Profile.create(
    {
      emailLower,
      email: e,
      sub: userId,
      ...patch,
    } as any,
    { authMode: 'userPool' } as any
  );
}

/** Convenience: save DCS results to Profile. */
export async function saveDCS({
  userId,
  email,
  dcsScores,
  dcsVersion,
}: {
  userId: string;
  email: string;
  dcsScores: string[];   // e.g., ["Avoidance: 12 (Strong)", ...]
  dcsVersion: string;    // e.g., "DCS-v2"
}) {
  return upsertMyProfile({
    userId,
    email,
    patch: {
      dcsScores,
      dcsVersion,
    } as Partial<Profile>,
  });
}

/** Convenience: save DPI results to Profile. */
export async function saveDPI({
  userId,
  email,
  dpiType,
  dpiResults,
  dpiVersion,
}: {
  userId: string;
  email: string;
  dpiType: string;       // e.g., "ENTJ"
  dpiResults: string[];  // compact item-level audit trail if you keep it
  dpiVersion: string;    // e.g., "DPI-v1"
}) {
  return upsertMyProfile({
    userId,
    email,
    patch: {
      dpiType,
      dpiResults,
      dpiVersion,
    } as Partial<Profile>,
  });
}
